/**
 * WYSIWYG 리치 텍스트 에디터 — 일정 '내용', 출장일지 '작업 내용'·'후속 조치' 처럼
 * 길게 쓰는 본문 필드 전용. 저장 포맷은 **마크다운 문자열** 이라 기존 text 컬럼을
 * 그대로 쓰며(백엔드 변경 없음), 과거에 저장된 평문도 그대로 읽힌다.
 *
 * 두 가지 모드:
 *  - 편집 모드(기본): 상단 툴바로 굵게/헤더/목록 등을 클릭 적용. 동시에 `## `,
 *    `- `, `> `, `**굵게**` 같은 마크다운을 직접 타이핑해도 즉시 서식으로 변환된다
 *    (StarterKit input rules).
 *  - readOnly: 툴바 없이 저장된 마크다운을 서식 그대로 렌더 — 상세 보기 다이얼로그용.
 *
 * 단일 source-of-truth 로 추출한 이유: 편집 폼과 상세 보기가 동일한 렌더 규칙
 * (헤더 크기/목록 들여쓰기 등)을 공유해야 작성한 그대로 보이기 때문.
 */
import React, { useEffect, useRef } from 'react';
import { Box, Divider, IconButton, Stack, ToggleButton, Tooltip } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  StrikethroughS as StrikeIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as OrderedIcon,
  FormatQuote as QuoteIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Movie as MovieIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import GlobalDragHandle from 'tiptap-extension-global-drag-handle';
import { MediaImage } from './MediaImage';
import { createMention, MentionUser } from './mention/createMention';

export interface RichTextEditorProps {
  /** 마크다운 문자열. 편집 결과도 마크다운으로 돌려준다. */
  value: string;
  /** 편집 모드에서 내용이 바뀔 때마다 마크다운 문자열로 호출. readOnly 면 불필요. */
  onChange?: (markdown: string) => void;
  /** true 면 툴바 없이 보기 전용 렌더. */
  readOnly?: boolean;
  /** 빈 입력일 때 표시할 안내문. */
  placeholder?: string;
  /** 편집 영역 최소 높이(px). 기본 180. readOnly 는 무시. */
  minHeight?: number;
  /** 검증 실패 시 테두리를 강조. */
  error?: boolean;
  /**
   * 이미지·동영상 파일을 업로드하고 본문에 저장할 **토큰 없는 src 경로**를 돌려준다.
   * 제공되면 툴바에 이미지·동영상 버튼이 생기고, 붙여넣기·드래그&드롭으로도 첨부된다.
   * 미제공이면 첨부 UI 는 숨겨진다(보기 전용/업로드 불가 폼).
   * (이름은 호환을 위해 유지 — 이미지/동영상 공용 업로드 콜백)
   */
  onUploadImage?: (file: File) => Promise<string>;
  /**
   * 제공되면 `@` 입력 시 사용자 멘션이 활성화된다. 멘션은 본문에
   * `@[이름](user:id)` 로 저장되어 백엔드가 알림을 보낸다.
   */
  mentionUsers?: MentionUser[];
}

const HEADING_LEVELS = [1, 2, 3] as const;

/** tiptap-markdown 의 storage 는 타입 augmentation 이 없어 직접 좁혀서 읽는다. */
function getMarkdown(ed: Editor): string {
  const storage = ed.storage as { markdown?: { getMarkdown: () => string } };
  return storage.markdown?.getMarkdown() ?? '';
}

/** ProseMirror 본문 — 헤더/목록/인용 등이 보이도록 한 공통 타이포 규칙. */
const proseSx = {
  '& .ProseMirror': {
    outline: 'none',
    wordBreak: 'break-word',
    '& > *:first-of-type': { mt: 0 },
    '& > *:last-child': { mb: 0 },
    '& h1': { fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.3, mt: 2, mb: 1 },
    '& h2': { fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.3, mt: 2, mb: 1 },
    '& h3': { fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.3, mt: 1.5, mb: 0.75 },
    '& p': { my: 0.75 },
    '& ul, & ol': { pl: 3, my: 0.75 },
    '& li': { my: 0.25 },
    '& li > p': { my: 0 },
    '& blockquote': {
      borderLeft: '3px solid',
      borderColor: 'divider',
      pl: 1.5,
      ml: 0,
      color: 'text.secondary',
      fontStyle: 'italic',
    },
    '& code': {
      bgcolor: 'action.hover',
      px: 0.5,
      py: 0.125,
      borderRadius: 0.5,
      fontSize: '0.875em',
    },
    '& pre': {
      bgcolor: 'action.hover',
      p: 1.5,
      borderRadius: 1,
      overflowX: 'auto',
      '& code': { bgcolor: 'transparent', p: 0 },
    },
    '& a': { color: 'primary.main', textDecoration: 'underline' },
    '& img, & video': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: 1,
      my: 1,
      display: 'block',
    },
    '& .mention': {
      color: 'primary.main',
      bgcolor: 'action.selected',
      borderRadius: 0.5,
      px: 0.5,
      fontWeight: 600,
    },
    '& img.ProseMirror-selectednode': {
      outline: '2px solid',
      outlineColor: 'primary.main',
    },
  },
} as const;

interface ToolbarProps {
  editor: Editor;
  /** 제공되면 이미지 삽입 버튼을 노출. 클릭 시 파일 선택 창을 연다. */
  onPickImage?: () => void;
  /** 제공되면 동영상 삽입 버튼을 노출. 클릭 시 파일 선택 창을 연다. */
  onPickVideo?: () => void;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, onPickImage, onPickVideo }) => {
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('링크 주소를 입력하세요 (비우면 링크 해제)', prev ?? 'https://');
    if (url === null) return; // 취소
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      // 좁은 화면에서 wrap 하면 툴바가 3줄로 늘어나 입력 영역을 다 밀어낸다.
      // 한 줄 유지 + 가로 스크롤로 바꿨다 — 넓은 화면에서는 전부 들어가므로
      // 스크롤바가 나타나지 않는다.
      flexWrap="nowrap"
      sx={{
        px: 0.5,
        py: 0.5,
        gap: 0.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        overflowX: 'auto',
        // 터치 스크롤에서 버튼이 눌리며 스크롤이 씹히지 않게.
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { height: 4 },
        // 아이콘 버튼이 스크롤 컨테이너에서 찌그러지지 않도록.
        '& > *': { flexShrink: 0 },
      }}
    >
      {HEADING_LEVELS.map((level) => (
        <ToggleButton
          key={level}
          value={`h${level}`}
          size="small"
          selected={editor.isActive('heading', { level })}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          sx={{ border: 0, px: 1, py: 0.25, fontWeight: 700, lineHeight: 1 }}
          aria-label={`헤더 ${level}`}
        >
          H{level}
        </ToggleButton>
      ))}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <ToolButton
        title="굵게"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="기울임"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="밑줄"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="취소선"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <StrikeIcon fontSize="small" />
      </ToolButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <ToolButton
        title="글머리 목록"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <BulletIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="번호 목록"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <OrderedIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="인용"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <QuoteIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="코드"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <CodeIcon fontSize="small" />
      </ToolButton>
      <ToolButton title="링크" active={editor.isActive('link')} onClick={setLink}>
        <LinkIcon fontSize="small" />
      </ToolButton>
      {onPickImage && (
        <ToolButton title="이미지 첨부" onClick={onPickImage}>
          <ImageIcon fontSize="small" />
        </ToolButton>
      )}
      {onPickVideo && (
        <ToolButton title="동영상 첨부" onClick={onPickVideo}>
          <MovieIcon fontSize="small" />
        </ToolButton>
      )}
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <ToolButton
        title="실행 취소"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <UndoIcon fontSize="small" />
      </ToolButton>
      <ToolButton
        title="다시 실행"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <RedoIcon fontSize="small" />
      </ToolButton>
    </Stack>
  );
};

interface ToolButtonProps {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const ToolButton: React.FC<ToolButtonProps> = ({ title, active, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onClick}
        color={active ? 'primary' : 'default'}
        sx={{ borderRadius: 1, bgcolor: active ? 'action.selected' : 'transparent' }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  placeholder,
  minHeight = 180,
  error = false,
  onUploadImage,
  mentionUsers,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  // 멘션 후보는 비동기 로드되므로 ref 로 최신값을 suggestion 에 노출(에디터는 한 번만 생성).
  const mentionUsersRef = useRef<MentionUser[]>(mentionUsers ?? []);
  mentionUsersRef.current = mentionUsers ?? [];
  // editorProps 핸들러는 useEditor 생성 시 한 번 캡처되므로, 최신 업로드 함수·에디터
  // 인스턴스를 ref 로 노출해 stale closure 를 피한다.
  const uploadRef = useRef(onUploadImage);
  const editorRef = useRef<Editor | null>(null);
  useEffect(() => {
    uploadRef.current = onUploadImage;
  }, [onUploadImage]);

  // 이미지/동영상 파일 업로드 후 본문에 삽입. pos 가 있으면(드롭) 그 위치에, 없으면 커서에.
  // 동영상은 저장 포맷을 이미지와 동일한 마크다운(`![](src)`)으로 유지하되, src 에
  // `?kind=video` 마커를 붙여 렌더 시 MediaImage 가 <video> 로 분기하게 한다.
  const insertMediaFile = async (file: File, pos?: number): Promise<void> => {
    const upload = uploadRef.current;
    const ed = editorRef.current;
    if (!upload || !ed) return;
    const isVideo = file.type.startsWith('video/');
    if (!file.type.startsWith('image/') && !isVideo) return;
    try {
      let src = await upload(file);
      if (isVideo) {
        src += `${src.includes('?') ? '&' : '?'}kind=video`;
      }
      if (typeof pos === 'number') {
        ed.chain().focus().insertContentAt(pos, { type: 'image', attrs: { src } }).run();
      } else {
        ed.chain().focus().setImage({ src }).run();
      }
    } catch (e) {
      window.alert(e instanceof Error ? e.message : '첨부 업로드에 실패했습니다.');
    }
  };
  const insertMediaRef = useRef(insertMediaFile);
  insertMediaRef.current = insertMediaFile;

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: readOnly, autolink: true },
      }),
      MediaImage,
      // 편집 모드에서만 단락(블록) 드래그 핸들을 붙인다 — hover 시 좌측 ⋮⋮ 그립으로
      // 문단/제목/목록/이미지를 끌어 순서를 바꾼다. readOnly(상세 보기)에는 불필요.
      ...(readOnly ? [] : [GlobalDragHandle.configure({ dragHandleWidth: 20 })]),
      // `@` 멘션 — mentionUsers 가 주어질 때만. 본문에 user_id 를 보존해 알림 발송.
      ...(mentionUsers ? [createMention(() => mentionUsersRef.current)] : []),
      Markdown.configure({ html: false, linkify: true, transformPastedText: true }),
    ],
    content: value || '',
    editorProps: {
      handlePaste: (_view, event) => {
        if (readOnly || !uploadRef.current) return false;
        const files = Array.from(event.clipboardData?.files ?? []).filter(
          (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
        );
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => void insertMediaRef.current(f));
        return true;
      },
      handleDrop: (view, event) => {
        if (readOnly || !uploadRef.current) return false;
        const dropped = (event as DragEvent).dataTransfer?.files;
        const files = Array.from(dropped ?? []).filter(
          (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
        );
        if (files.length === 0) return false;
        event.preventDefault();
        const pos = view.posAtCoords({
          left: (event as DragEvent).clientX,
          top: (event as DragEvent).clientY,
        })?.pos;
        files.forEach((f) => void insertMediaRef.current(f, pos));
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(getMarkdown(ed));
    },
  });
  editorRef.current = editor;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((f) => void insertMediaRef.current(f));
    e.target.value = ''; // 같은 파일 재선택 허용
  };

  // 외부 value 가 바뀌면(다이얼로그 열림/수정 대상 전환/보기 갱신) 에디터에 반영.
  // 단, 사용자가 입력 중(focus)일 때는 덮어쓰지 않는다.
  useEffect(() => {
    if (!editor) return;
    const current = getMarkdown(editor);
    if (value === current) return;
    if (editor.isFocused) return;
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  // readOnly 토글이 바뀌면 editable 동기화.
  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  if (readOnly) {
    return (
      <Box sx={proseSx}>
        <EditorContent editor={editor} />
      </Box>
    );
  }

  const showPlaceholder = !!placeholder && editor?.isEmpty;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        transition: 'border-color 120ms',
        '&:focus-within': { borderColor: error ? 'error.main' : 'primary.main' },
      }}
    >
      {editor && (
        <Toolbar
          editor={editor}
          onPickImage={onUploadImage ? () => fileInputRef.current?.click() : undefined}
          onPickVideo={onUploadImage ? () => videoInputRef.current?.click() : undefined}
        />
      )}
      {onUploadImage && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFileChange}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            multiple
            hidden
            onChange={handleFileChange}
          />
        </>
      )}
      <Box sx={{ position: 'relative', ...proseSx }}>
        {showPlaceholder && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              left: 14,
              color: 'text.disabled',
              pointerEvents: 'none',
              fontSize: '0.95rem',
            }}
          >
            {placeholder}
          </Box>
        )}
        <Box
          sx={{
            p: 1.5,
            minHeight,
            maxHeight: 420,
            overflowY: 'auto',
            cursor: 'text',
            '& .ProseMirror': { minHeight },
          }}
          onClick={() => editor?.chain().focus().run()}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Box>
  );
};

export default RichTextEditor;
